import { PrimaryButton } from "@components/Buttons";
import { DocumentInfoIcon } from "@components/Icons";
import { ComponentHeader } from "@components/Tables/ComponentHeader";
import TableWithPagination from "@components/Tables/TableWithPagination";
import { VerificationResultSelectionModal } from "@components/UI";
import { VulnerabilityBadge } from "@components/UI/Badge";
import { useTheme } from "@hooks/useTheme";
import { formatDateTime, formatVerificationType } from "@utils/common";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import useVulnerabilityPolicy from "@hooks/useVulnerabilityPolicy";
import ComponentConflictStatusGuideModal from "@components/UI/ComponentConflictStatusGuideModal";
import { VulnerabilityDetailModal, ComponentInformationModal, LicenseInformationModal } from "@components/UI";
import UserNameWithId from "@components/UI/UserNameWithId";
import { useMutationData } from "@hooks/useMutationData";

const VerificationComparisonInfo = ({
  type,
  selectedVerificationResult,
  excludedVerificationResult = null,
  componentData,
  onSelect,
  verificationNumber,
  onProjectClick,
}) => {
  const tid = (suffix) => `verification-comparison-info-${verificationNumber}-${suffix}`;
  const fieldContainerTestId = (name) => tid(`field-${name}-container`);
  const fieldLabelTestId = (name) => tid(`field-${name}-label`);
  const fieldValueTestId = (name) => tid(`field-${name}-value`);

  const { colors } = useTheme();
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const { vulnerabilityLevel } = useVulnerabilityPolicy();
  const [isVulnerabilityModalOpen, setIsVulnerabilityModalOpen] = useState(false);
  const [selectedVulnerabilityData, setSelectedVulnerabilityData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isComponentModalOpen, setIsComponentModalOpen] = useState(false);
  const [selectedComponentData, setSelectedComponentData] = useState(null);
  const [isLicenseInformationModalOpen, setIsLicenseInformationModalOpen] = useState(false);
  const [selectedLicenseInformationData, setSelectedLicenseInformationData] = useState(null);

  const renderInfoField = (fieldName, label, value, { title, onValueClick, valueClassName = "" } = {}) => (
    <div className="flex flex-col" data-testid={fieldContainerTestId(fieldName)}>
      <label style={{ fontSize: "0.8125rem" }} className={`font-normal ${colors.textSubTitle} mb-1`} data-testid={fieldLabelTestId(fieldName)}>
        {label}
      </label>
      <p
        className={`text-sm font-medium truncate w-full min-w-0 ${valueClassName || colors.textPrimary}`}
        title={title ?? value ?? "---"}
        data-testid={fieldValueTestId(fieldName)}
        onClick={onValueClick}
      >
        {value ?? "---"}
      </p>
    </div>
  );

  const handleModalClose = () => {
    setShowModal(false);
  };

  const handleSelectClick = () => {
    setShowModal(true);
  };

  const getVulnerabilityDataMutation = useMutationData(
    (levelCode) => `/vulnerability_policy/level_code/${levelCode}`,
    { method: "GET" },
    {
      onSuccess: (response, levelCode) => {
        setSelectedVulnerabilityData({ ...response, vulnerabilityLevel: levelCode });
        setIsVulnerabilityModalOpen(true);
      },
      onError: (error) => {
        console.error("Error:", error);
      },
    },
  );

  const handleVulnerabilityClick = async (vulnerabilityLevelCode) => {
    if (vulnerabilityLevelCode) {
      await getVulnerabilityDataMutation.mutateAsync(vulnerabilityLevelCode);
    }
  };

  const totalPages = Math.ceil((componentData?.length || 0) / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedComponentData = componentData?.slice(startIndex, endIndex) || [];

  useEffect(() => {
    setCurrentPage(1);
  }, [componentData?.length]);

  const componentListColumns = ["component", "severity", "vulnerabilityLevel"];

  const componentListColumnConfig = {
    component: {
      headerRender: () => <ComponentHeader />,
      customRender: (value, row) => {
        const componentVersion = row.componentVersion || "";
        const licenseIdentifier = row.componentLicense || row.licenseIdentifier || row.licenseName;
        const isValidLicense = licenseIdentifier != null && licenseIdentifier.toUpperCase() !== "N/A";
        const isVersionLong = componentVersion.length > 15;
        const componentName = type === "preview" ? row.componentName : row.component || "";
        const rowId = row.componentSeq ?? row.component ?? "unknown";

        const handleLicenseClick = (e) => {
          e.stopPropagation();
          if (isValidLicense) {
            setSelectedLicenseInformationData(licenseIdentifier);
            setIsLicenseInformationModalOpen(true);
          }
        };

        const handleComponentClick = (e) => {
          e.stopPropagation();
          setSelectedComponentData(row);
          setIsComponentModalOpen(true);
        };

        return (
          <div className="flex items-center gap-1.5 w-full min-w-0 h-8" data-testid={tid(`component-row-${rowId}`)}>
            <div className="flex-1 min-w-0 overflow-hidden" data-testid={tid(`component-row-${rowId}-content`)}>
              <div
                className={`text-sm font-medium ${colors.textComparisonPrimary} cursor-pointer ${colors.hoverTextAccentCyan} transition-colors truncate`}
                onClick={handleComponentClick}
                title={`${componentName} (${componentVersion})`}
                data-testid={tid(`component-row-${rowId}-name`)}
              >
                <span className={`${colors.textPrimary}`}>{componentName}</span>
                <span className={colors.textComparisonMuted}>
                  {` (`}
                  {isVersionLong ? (
                    <span className="truncate max-w-[120px] inline-block align-bottom" title={componentVersion}>
                      {componentVersion}
                    </span>
                  ) : (
                    componentVersion
                  )}
                  {`)`}
                </span>
              </div>
              <div
                className={`mt-0.5 text-xs truncate ${isValidLicense ? `cursor-pointer ${colors.textComparisonLink} ${colors.hoverTextComparisonLink} transition-colors` : colors.textComparisonMuted}`}
                onClick={isValidLicense ? handleLicenseClick : undefined}
                title={licenseIdentifier || ""}
                data-testid={tid(`component-row-${rowId}-license`)}
              >
                {licenseIdentifier || ""}
              </div>
            </div>
          </div>
        );
      },
      style: { width: "40%" },
    },
    severity: {
      headerRender: () => t("status.severity"),
      customRender: (value, row) => {
        const rowId = row.componentSeq ?? row.component ?? "unknown";
        if (!row.cvssScore) {
          return (
            <span className={`text-sm ${colors.textComparisonMuted} w-1/4`} data-testid={tid(`component-row-${rowId}-severity-empty`)}>
              ---
            </span>
          );
        }
        const displayValue = row.cvssScore.charAt(0).toUpperCase() + row.cvssScore.slice(1);
        return (
          <span className={`text-sm ${colors.textComparisonPrimary} w-1/4`} data-testid={tid(`component-row-${rowId}-severity`)}>
            {displayValue}
          </span>
        );
      },
    },
    vulnerabilityLevel: {
      style: {
        whiteSpace: "normal",
        textAlign: "center",
        overflowWrap: "initial",
        textTransform: "uppercase"
      },
      headerRender: () => (
        <ComponentConflictStatusGuideModal
          title={t("vulnerability-policy.vulnerability-security-code")}
          colors={colors}
          t={t}
          showObligationLevel={false}
        />
      ),
      customRender: (value, row) => {
        const rowId = row.componentSeq ?? row.component ?? "unknown";
        const matchedData = vulnerabilityLevel.find(item => item.levelCode === value);
        if (matchedData) {
          return (
            <VulnerabilityBadge
              level={value}
              bgColor={matchedData.vulnerabilityLevel}
              className="font-extrabold"
              data-testid={tid(`component-row-${rowId}-vulnerability-badge`)}
              onClick={() => {
                handleVulnerabilityClick(value);
              }}
            />
          );
        }
        return (
          <span className={`text-sm ${colors.textComparisonMuted}`} data-testid={tid(`component-row-${rowId}-vulnerability-empty`)}>
            ---
          </span>
        );
      },
    },
  };

  const isPreview = type === "preview";
  const projectLabel = `${t(isPreview ? "project.project-preview-name" : "common.project-name")}:`;
  const projectDisplayName = isPreview
    ? selectedVerificationResult.projectPreviewName || "---"
    : selectedVerificationResult.projectName || "---";

  return (
    <div className="min-w-0 w-full" data-testid={tid("panel")}>
      <h2 className={`text-lg font-semibold ${colors.textPrimary} mb-4 uppercase`} data-testid={tid("title")}>
        {t("verification-comparison.verification")} {verificationNumber}
      </h2>

      {/* File Information */}
      <div
        className={`mb-4 p-3 border border-dashed ${colors.borderOutLineButton} rounded-lg ${colors.bgAddVerificationFileBtn}`}
        data-testid={tid("file-info")}
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" data-testid={tid("file-info-content")}>
          <div className="flex items-center space-x-2 min-w-0 w-full sm:flex-1 overflow-hidden" data-testid={tid("file-info-left")}>
            {type === "project" ? (
              <>
                <DocumentInfoIcon size={16} className={`${colors.textNormal} flex-shrink-0`} data-testid={tid("file-icon")} />
                <span
                  className={`text-sm font-medium ${colors.textNormal} truncate min-w-0`}
                  title={selectedVerificationResult.realFileName}
                  data-testid={tid("file-name")}
                >
                  {selectedVerificationResult.realFileName}
                </span>
              </>
            ) : null}
          </div>
          <PrimaryButton
            size="sm"
            data-testid={tid("select-button")}
            onClick={handleSelectClick}
            className={`${colors.buttonComparisonSelect} flex-shrink-0 whitespace-nowrap`}
          >
            {verificationNumber === 1
              ? t("verification-comparison.select-verification-result-1")
              : t("verification-comparison.select-verification-result-2")}
          </PrimaryButton>
        </div>
      </div>

      {/* Verification Details */}
      <div data-testid={tid("details")}>
        <div className="grid grid-cols-3 gap-4" data-testid={tid("details-grid")}>
          {/* Version */}
          {renderInfoField("version", `${t("common.version")}:`, selectedVerificationResult.verificationVersion)}
          {/* Verif. Type */}
          {renderInfoField(
            "verification-type",
            `${t("common.verification-type")}:`,
            formatVerificationType(selectedVerificationResult.verificationType),
          )}
          {/* Content Name */}
          {renderInfoField(
            "real-file-name",
            `${t("common.verif-real-file-name")}:`,
            selectedVerificationResult.realFileName,
            { title: selectedVerificationResult.realFileName || "---" },
          )}
          {/* Requester */}
          <div className="flex min-w-0 flex-col" data-testid={fieldContainerTestId("requester")}>
            <label
              style={{ fontSize: "0.8125rem" }}
              className={`mb-1 font-normal ${colors.textSubTitle}`}
              data-testid={fieldLabelTestId("requester")}
            >
              {`${t("common.requester")}:`}
            </label>
            <UserNameWithId
              name={
                selectedVerificationResult.requesterName ||
                selectedVerificationResult.createrName
              }
              id={
                selectedVerificationResult.requester ||
                selectedVerificationResult.requesterId ||
                selectedVerificationResult.createrId
              }
              layout="inline"
              data-testid={fieldValueTestId("requester")}
            />
          </div>
          {/* Requested At */}
          {renderInfoField(
            "requested-at",
            `${t("common.requested-at")}:`,
            formatDateTime(selectedVerificationResult.requestedAt),
          )}
          {/* Project Name */}
          {renderInfoField(
            isPreview ? "project-preview-name" : "project-name",
            projectLabel,
            projectDisplayName,
            {
              title: projectDisplayName,
              valueClassName: isPreview ? "" : `${colors.textActive} cursor-pointer hover:underline`,
              onValueClick: !isPreview ? () => onProjectClick?.(selectedVerificationResult.projectSeq) : undefined,
            },
          )}
        </div>
      </div>

      {/* Component List */}
      {selectedVerificationResult && (
        <>
          <h3
            className={`mt-6 pt-6 pb-3 text-md font-semibold ${colors.textPrimary} border-t ${colors.divider} -mx-6 px-6 uppercase`}
            data-testid={tid("component-list-title")}
          >
            {t("common.component-list")}
          </h3>
          <div className="rounded-lg overflow-hidden" data-testid={tid("component-list-section")}>
            <div className="overflow-x-auto" data-testid={tid("component-list-table-wrapper")}>
              <TableWithPagination
                columns={componentListColumns}
                columnConfig={componentListColumnConfig}
                data={paginatedComponentData}
                onRowClick={() => { }}
                loading={false}
                totalPages={totalPages}
                totalData={componentData?.length || 0}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                maxRowsPerPage={itemsPerPage}
                hasCheckbox={false}
                hasRadioButton={false}
                rowKey="componentSeq"
                shouldShowGoToPage={true}
                showPagination={true}
                onItemsPerPageChange={(value) => {
                  setItemsPerPage(value);
                  setCurrentPage(1);
                }}
                showItemsPerPageSelector={true}
                dataTestId={tid("component-table")}
              />
            </div>
          </div>
        </>
      )}

      <VerificationResultSelectionModal
        data-testid={tid("selection-modal")}
        isOpen={showModal}
        onClose={handleModalClose}
        onSelect={(selectedItem) => {
          onSelect(selectedItem);
          setShowModal(false);
        }}
        selectedVerificationResult={selectedVerificationResult}
        excludedVerificationResult={excludedVerificationResult}
      />
      <VulnerabilityDetailModal
        data-testid={tid("vulnerability-modal")}
        isOpen={isVulnerabilityModalOpen}
        onClose={() => {
          setIsVulnerabilityModalOpen(false);
          setSelectedVulnerabilityData(null);
        }}
        data={selectedVulnerabilityData}
      />
      <ComponentInformationModal
        data-testid={tid("component-modal")}
        show={isComponentModalOpen}
        onClose={() => {
          setIsComponentModalOpen(false);
          setSelectedComponentData(null);
        }}
        componentData={selectedComponentData}
      />
      <LicenseInformationModal
        data-testid={tid("license-modal")}
        show={isLicenseInformationModalOpen}
        onClose={() => {
          setIsLicenseInformationModalOpen(false);
          setSelectedLicenseInformationData(null);
        }}
        componentLicense={selectedLicenseInformationData}
      />
    </div>
  );
};

export default VerificationComparisonInfo;
